/**
 * /token-intelligence — AUTHED dashboard combining T1+T3+T9.
 * =============================================================
 * Three panels stacked:
 *   1. Token Leak Detector (T1) — runaway requests in last N hours
 *   2. Cache-Miss Detective (T3) — features eligible for prompt caching
 *   3. Per-Customer Token Heatmap (T9) — 2-D customer × feature matrix
 *
 * Brand: Slate-950 page, emerald accents, soft amber for warnings.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  AlertTriangle, Loader2, RefreshCcw, TrendingDown, Database, Layers,
  Copy, Check, ArrowRight, ShieldCheck, Sparkles, Wand2,
  Repeat, Activity, Minimize2, Pause, Route, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n, d = 2) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: d })}`;

// Heatmap cell colour: emerald for low, amber mid, rose high.
const cellColor = (pct) => {
  if (pct >= 30) return 'bg-rose-500/30 text-rose-100 border-rose-500/40';
  if (pct >= 15) return 'bg-amber-500/20 text-amber-100 border-amber-500/30';
  if (pct >= 5)  return 'bg-emerald-500/15 text-emerald-100 border-emerald-500/30';
  if (pct > 0)   return 'bg-slate-800 text-slate-300 border-white/5';
  return 'bg-slate-900 text-slate-400 border-white/5';
};

const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// ── Paywall + usage UI primitives ─────────────────────────────────────────
const PaywallCard = ({ featureName, message, onUpgrade }) => (
  <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-5 text-center" data-testid="paywall-card">
    <Lock className="w-7 h-7 text-emerald-300 mx-auto mb-3" />
    <div className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">CARTIE Pro</div>
    <h4 className="text-lg font-semibold text-white mt-1">{featureName}</h4>
    <p className="text-sm text-slate-300 mt-2 max-w-md mx-auto">{message}</p>
    <Button
      onClick={onUpgrade}
      className="mt-4 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
      data-testid="paywall-upgrade-btn"
    >
      Upgrade to Pro <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
    </Button>
  </div>
);

const UsageBadge = ({ quota }) => {
  if (!quota || quota.pro) return null;
  const pct = quota.limit > 0 ? (quota.used / quota.limit) * 100 : 0;
  const tone = pct >= 80 ? 'text-amber-300 border-amber-400/40' : 'text-slate-400 border-white/10';
  return (
    <span className={`text-[11px] uppercase tracking-wider font-semibold ${tone} border rounded-full px-2 py-0.5`} data-testid="usage-badge">
      {quota.used} / {quota.limit} this month
    </span>
  );
};

// Safe copy-to-clipboard for sandboxed iframes / restricted contexts.
// The Clipboard API rejects in many preview environments (NotAllowedError) —
// fall back to the legacy execCommand approach so we never throw an
// unhandled rejection that triggers React's red error overlay.
const safeCopyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
};

const LeakDetector = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(720);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/token-leak-detector/scan`, {
        headers: authHeaders(), params: { hours },
      });
      setData(data);
    } catch (e) {
      toast.error('Could not scan for leaks');
    } finally { setLoading(false); }
  }, [hours]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="bg-slate-900/60 border-white/10" data-testid="leak-detector-card">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-300" /> Token Leak Detector
              <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">
                T1
              </Badge>
            </CardTitle>
            <CardDescription className="text-slate-400">
              Runaway requests in the last {hours / 24 | 0} days. Each leak = a single call costing 2× the rolling median for that feature.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              className="bg-slate-950 border border-white/10 text-slate-100 text-xs rounded-md px-2 py-1"
              data-testid="leak-window-select"
            >
              <option value={24}>24h</option>
              <option value={168}>7d</option>
              <option value={720}>30d</option>
            </select>
            <Button size="sm" variant="ghost" onClick={load} className="text-slate-400 h-8" data-testid="leak-refresh">
              <RefreshCcw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Scanning…
          </div>
        ) : !data || data.events_scanned === 0 ? (
          <p className="text-slate-400 text-sm py-6 text-center" data-testid="leak-no-data">
            No AI events yet. POST events to <code className="text-emerald-300">/api/ai-cost-events/track</code> to start scanning.
          </p>
        ) : (
          <>
            <div className="grid sm:grid-cols-3 gap-3 mb-4" data-testid="leak-summary">
              <div className="bg-slate-950 border border-white/10 rounded-md p-3">
                <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">Leaks found</p>
                <p className="text-2xl font-bold tabular-nums text-rose-300" data-testid="leak-count">{data.leaks_found}</p>
              </div>
              <div className="bg-slate-950 border border-white/10 rounded-md p-3">
                <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">$ potentially wasted</p>
                <p className="text-2xl font-bold tabular-nums text-rose-300" data-testid="leak-waste">{fmt(data.dollars_potentially_wasted)}</p>
              </div>
              <div className="bg-slate-950 border border-white/10 rounded-md p-3">
                <p className="text-[11px] uppercase tracking-widest text-slate-400 mb-1">Events scanned</p>
                <p className="text-2xl font-bold tabular-nums text-slate-100">{data.events_scanned.toLocaleString()}</p>
              </div>
            </div>
            {data.leaks?.length > 0 ? (
              <div className="space-y-2" data-testid="leak-list">
                {data.leaks.map((leak, idx) => (
                  <div key={idx} className="bg-rose-500/5 border border-rose-500/20 rounded-md p-3 flex items-start justify-between gap-3" data-testid={`leak-row-${idx}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <code className="text-rose-200 font-semibold">{leak.feature_slug}</code>
                        {leak.customer_id && <span className="text-slate-400 text-xs">· {leak.customer_id}</span>}
                        {leak.model && <Badge variant="outline" className="border-emerald-400/30 text-emerald-300 text-[11px]">{leak.model}</Badge>}
                      </div>
                      <p className="text-xs text-slate-400">
                        {leak.reasons.join(' · ')}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-lg font-bold tabular-nums text-rose-300">{fmt(leak.cost_usd, 2)}</p>
                      {leak.ratio_vs_median && <p className="text-[11px] text-slate-400">{leak.ratio_vs_median}× median</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-emerald-300 text-sm flex items-center gap-2 py-3">
                <ShieldCheck className="w-4 h-4" /> No leaks in window — your AI traffic looks healthy.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const CacheMissDetective = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedSnippet, setCopiedSnippet] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/cache-miss-detective/scan`, {
        headers: authHeaders(), params: { days: 30 },
      });
      setData(data);
    } catch (e) {
      toast.error('Could not run cache analysis');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copySnippet = async (which) => {
    const ok = await safeCopyToClipboard(which === 'py' ? data.code_snippet_python : data.code_snippet_js);
    if (ok) {
      setCopiedSnippet(which);
      toast.success('Snippet copied');
      setTimeout(() => setCopiedSnippet(null), 1500);
    } else {
      toast.error('Copy failed — please select & copy manually');
    }
  };

  return (
    <Card className="bg-slate-900/60 border-white/10" data-testid="cache-miss-card">
      <CardHeader>
        <CardTitle className="text-base text-white flex items-center gap-2">
          <Database className="w-4 h-4 text-emerald-300" /> Cache-Miss Detective
          <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">T3</Badge>
        </CardTitle>
        <CardDescription className="text-slate-400">
          Anthropic prompt caching can save 90% on input tokens. We find features that qualify.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Analyzing…
          </div>
        ) : !data || data.features_analyzed === 0 ? (
          <p className="text-slate-400 text-sm py-6 text-center" data-testid="cache-no-data">
            No AI traffic to analyze yet.
          </p>
        ) : (
          <>
            <div className="mb-4 bg-emerald-500/10 border border-emerald-500/30 rounded-md p-3 flex items-center justify-between" data-testid="cache-projection">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-emerald-300 mb-1">Projected annual savings</p>
                <p className="text-3xl font-bold tabular-nums text-emerald-300" data-testid="cache-annual-savings">{fmt(data.total_projected_annual_savings_usd, 0)}</p>
              </div>
              <p className="text-xs text-slate-400 text-right max-w-[200px]">
                {data.cache_candidates.length} features eligible<br />
                across {data.features_analyzed} analyzed
              </p>
            </div>

            {data.cache_candidates.length > 0 ? (
              <div className="space-y-2 mb-5" data-testid="cache-candidates">
                {data.cache_candidates.slice(0, 8).map((c, idx) => (
                  <div key={c.feature_slug} className="bg-slate-950 border border-white/10 rounded-md p-3" data-testid={`cache-row-${idx}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <code className="text-emerald-300 font-semibold">{c.feature_slug}</code>
                          <Badge variant="outline" className={`text-[11px] ${
                            c.verdict === 'high_priority' ? 'border-emerald-400/40 text-emerald-300'
                            : c.verdict === 'good_candidate' ? 'border-emerald-400/30 text-emerald-300'
                            : 'border-slate-500/30 text-slate-400'
                          }`}>
                            {c.verdict.replace('_', ' ')}
                          </Badge>
                          {!c.is_anthropic && <Badge variant="outline" className="border-amber-400/30 text-amber-200 text-[11px]">REQUIRES MODEL SWAP</Badge>}
                        </div>
                        <p className="text-xs text-slate-400 mt-1">
                          {c.calls_in_window.toLocaleString()} calls · {fmt(c.avg_cost_per_call_usd, 4)}/call · consistency {(c.cost_consistency_score * 100).toFixed(0)}%
                        </p>
                      </div>
                      <p className="text-lg font-bold tabular-nums text-emerald-300 flex-shrink-0">
                        +{fmt(c.projected_annual_savings_usd, 0)}/yr
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-sm py-3">
                No strong cache candidates yet. Keep streaming events — we need volume (≥100 calls per feature) to recommend.
              </p>
            )}

            {data.code_snippet_python && (
              <Tabs defaultValue="py" className="w-full">
                <TabsList className="bg-slate-900 border border-white/10">
                  <TabsTrigger value="py" data-testid="cache-tab-python">Python</TabsTrigger>
                  <TabsTrigger value="js" data-testid="cache-tab-js">JavaScript</TabsTrigger>
                </TabsList>
                <TabsContent value="py" className="mt-3">
                  <div className="relative">
                    <Button size="sm" variant="ghost" onClick={() => copySnippet('py')} className="absolute top-2 right-2 z-10 text-slate-400 h-7 px-2" data-testid="cache-copy-py">
                      {copiedSnippet === 'py' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                    <pre className="bg-slate-950 border border-white/10 rounded-lg p-4 overflow-x-auto text-xs text-emerald-100/90 font-mono leading-relaxed pr-12">
                      <code>{data.code_snippet_python}</code>
                    </pre>
                  </div>
                </TabsContent>
                <TabsContent value="js" className="mt-3">
                  <div className="relative">
                    <Button size="sm" variant="ghost" onClick={() => copySnippet('js')} className="absolute top-2 right-2 z-10 text-slate-400 h-7 px-2" data-testid="cache-copy-js">
                      {copiedSnippet === 'js' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                    <pre className="bg-slate-950 border border-white/10 rounded-lg p-4 overflow-x-auto text-xs text-emerald-100/90 font-mono leading-relaxed pr-12">
                      <code>{data.code_snippet_js}</code>
                    </pre>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const Heatmap = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/token-heatmap/scan`, {
        headers: authHeaders(), params: { days: 30 },
      });
      setData(data);
    } catch (e) {
      toast.error('Could not load heatmap');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="bg-slate-900/60 border-white/10" data-testid="heatmap-card">
      <CardHeader>
        <CardTitle className="text-base text-white flex items-center gap-2">
          <Layers className="w-4 h-4 text-emerald-300" /> Per-Customer Token Heatmap
          <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">T9</Badge>
        </CardTitle>
        <CardDescription className="text-slate-400">
          Customer × feature × $ spent. Find the BurnRate Co at a glance.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : !data || data.customers.length === 0 ? (
          <p className="text-slate-400 text-sm py-6 text-center" data-testid="heatmap-no-data">
            No customer-attributed events yet. Pass <code className="text-emerald-300">customer_id</code> when calling{' '}
            <code className="text-emerald-300">/api/ai-cost-events/track</code> to light this up.
          </p>
        ) : (
          <>
            {data.hottest_cell && (
              <div className="mb-4 bg-rose-500/10 border border-rose-500/30 rounded-md p-3 flex items-center justify-between flex-wrap gap-2" data-testid="heatmap-hottest">
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-rose-300 mb-1">Hottest cell</p>
                  <p className="text-lg font-semibold text-rose-200">
                    <code className="text-rose-300">{data.hottest_cell.customer_id}</code> on{' '}
                    <code className="text-rose-300">{data.hottest_cell.feature_slug}</code>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold tabular-nums text-rose-300">{fmt(data.hottest_cell.cost_usd, 0)}</p>
                  <p className="text-[11px] text-slate-400">{data.hottest_cell.pct_of_grand_total}% of total</p>
                </div>
              </div>
            )}
            <div className="overflow-x-auto" data-testid="heatmap-table-wrap">
              <table className="border-collapse text-xs">
                <thead>
                  <tr>
                    <th className="text-left text-[11px] uppercase tracking-widest text-slate-400 py-2 pr-3 sticky left-0 bg-slate-900/60">Customer</th>
                    {data.features.map((f) => (
                      <th key={f.slug} className="px-2 py-2 text-[11px] uppercase tracking-widest text-slate-400 font-medium">
                        <span className="block text-emerald-300">{f.slug}</span>
                        <span className="block text-slate-400 normal-case">{fmt(f.total_usd, 0)}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {data.matrix.map((row, ridx) => {
                    const cust = data.customers[ridx];
                    return (
                      <tr key={cust.id} data-testid={`heatmap-row-${ridx}`}>
                        <td className="py-1.5 pr-3 sticky left-0 bg-slate-900/60">
                          <span className="text-slate-100 text-sm font-medium">{cust.id}</span>
                          <span className="block text-slate-400 text-[11px]">{fmt(cust.total_usd, 0)}</span>
                        </td>
                        {row.map((cell, cidx) => (
                          <td key={cidx} className="px-1 py-1">
                            <div
                              className={`text-center rounded border px-2 py-1.5 tabular-nums text-xs ${cellColor(cell.pct_of_grand_total)}`}
                              data-testid={`heatmap-cell-${ridx}-${cidx}`}
                              title={`${cust.id} × ${cell.feature_slug}: ${fmt(cell.cost_usd, 2)} (${cell.pct_of_grand_total}% of total)`}
                            >
                              {cell.cost_usd > 0 ? fmt(cell.cost_usd, 0) : '—'}
                            </div>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-400 mt-3">
              Grand total: <span className="text-emerald-300 font-semibold">{fmt(data.grand_total_usd, 0)}</span> across {data.n_customers_total} customers × {data.n_features_total} features (last 30 days).
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─────────────────────── T6 · Multi-Model Smart Router (PRO / Headline) ───────────────
const MultiModelRouter = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('table');
  const [copied, setCopied] = useState(null);
  const [paywall, setPaywall] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/multi-model-router/scan`, {
        headers: authHeaders(), params: { days: 30 },
      });
      setData(data);
      setPaywall(null);
    } catch (e) {
      if (e?.response?.status === 402) {
        setPaywall(e.response.data?.detail || {});
      } else {
        toast.error('Could not run multi-model analysis');
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyCode = async (kind, code) => {
    const ok = await safeCopyToClipboard(code);
    if (ok) {
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
      toast.success('Copied');
    } else {
      toast.error('Copy failed — please select & copy manually');
    }
  };

  return (
    <Card className="bg-emerald-500/10 border-emerald-500/30" data-testid="multi-model-router-card">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Route className="w-4 h-4 text-emerald-300" /> Multi-Model Smart Router
              <Badge className="bg-emerald-500 text-slate-950 text-[11px] font-semibold">T6 · HEADLINE</Badge>
            </CardTitle>
            <CardDescription className="text-slate-300">
              Per-feature model recommendations + drop-in middleware code. The cheapest "good enough" model for every workload.
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="border-emerald-400/40 text-emerald-200" data-testid="mmr-refresh-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
          </Button>
        </div>
        <div className="mt-2"><UsageBadge quota={data?.quota} /></div>
      </CardHeader>
      <CardContent className="space-y-4">
        {paywall ? (
          <PaywallCard
            featureName="Multi-Model Smart Router"
            message={paywall.message || "Pro-only feature. Upgrade to unlock per-feature model recommendations + drop-in middleware code."}
            onUpgrade={() => { window.location.href = `${paywall.upgrade_url || '/pricing'}?from=token-intel&feature=multi-model-router`; }}
          />
        ) : loading && !data ? (
          <div className="text-slate-400 text-sm">Analyzing…</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-950/50 rounded-lg p-3 border border-white/5">
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Features</div>
                <div className="text-xl font-bold text-white mt-1">{data?.features_analyzed || 0}</div>
              </div>
              <div className="bg-slate-950/50 rounded-lg p-3 border border-emerald-500/30">
                <div className="text-[11px] uppercase tracking-wider text-emerald-300">Annual savings</div>
                <div className="text-xl font-bold text-emerald-200 mt-1" data-testid="mmr-annual-savings">{fmt(data?.total_projected_annual_savings_usd)}</div>
              </div>
              <div className="bg-slate-950/50 rounded-lg p-3 border border-white/5">
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Switches</div>
                <div className="text-xl font-bold text-white mt-1" data-testid="mmr-switch-count">
                  {data?.recommendations?.filter(r => r.verdict === 'switch').length || 0}
                </div>
              </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-slate-950 border border-white/5">
                <TabsTrigger value="table" data-testid="mmr-tab-table">Recommendations</TabsTrigger>
                <TabsTrigger value="python" data-testid="mmr-tab-python">Python middleware</TabsTrigger>
                <TabsTrigger value="js" data-testid="mmr-tab-js">JS middleware</TabsTrigger>
              </TabsList>

              <TabsContent value="table" className="mt-3">
                {data?.recommendations?.length > 0 ? (
                  <div className="rounded-lg border border-white/5 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-950/60 text-[11px] uppercase tracking-wider text-slate-400">
                        <tr>
                          <th className="px-3 py-2 text-left">Feature</th>
                          <th className="px-3 py-2 text-left">Current</th>
                          <th className="px-3 py-2 text-left">Recommended</th>
                          <th className="px-3 py-2 text-right">Save %</th>
                          <th className="px-3 py-2 text-right">$/yr</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.recommendations.slice(0, 10).map((r, i) => (
                          <tr key={i} className="border-t border-white/5" data-testid={`mmr-row-${i}`}>
                            <td className="px-3 py-2 font-mono text-xs text-slate-300">{r.feature_slug}</td>
                            <td className="px-3 py-2 text-slate-400 text-xs">{r.current_model_label || r.current_model}</td>
                            <td className="px-3 py-2">
                              {r.verdict === 'optimal' ? (
                                <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">already optimal</Badge>
                              ) : (
                                <span className="text-emerald-300 text-xs">{r.recommended_model_label}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right text-emerald-200 font-mono text-xs">
                              {r.projected_savings_pct ? `${r.projected_savings_pct}%` : '—'}
                            </td>
                            <td className="px-3 py-2 text-right text-emerald-200 font-mono">{fmt(r.projected_annual_savings_usd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-slate-400 text-sm p-4">No features with enough volume yet — seed sample data first.</div>
                )}
              </TabsContent>

              <TabsContent value="python" className="mt-3">
                <div className="relative">
                  <pre className="bg-slate-950 border border-white/10 rounded-lg p-4 text-xs text-emerald-100 overflow-x-auto font-mono" data-testid="mmr-python-code">
{data?.middleware_code_python}
                  </pre>
                  <Button
                    onClick={() => copyCode('py', data?.middleware_code_python)}
                    size="sm"
                    variant="outline"
                    className="absolute top-2 right-2 border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10"
                    data-testid="mmr-copy-python"
                  >
                    {copied === 'py' ? <><Check className="w-3.5 h-3.5 mr-1.5" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy</>}
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="js" className="mt-3">
                <div className="relative">
                  <pre className="bg-slate-950 border border-white/10 rounded-lg p-4 text-xs text-emerald-100 overflow-x-auto font-mono" data-testid="mmr-js-code">
{data?.middleware_code_js}
                  </pre>
                  <Button
                    onClick={() => copyCode('js', data?.middleware_code_js)}
                    size="sm"
                    variant="outline"
                    className="absolute top-2 right-2 border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10"
                    data-testid="mmr-copy-js"
                  >
                    {copied === 'js' ? <><Check className="w-3.5 h-3.5 mr-1.5" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy</>}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            <p className="text-xs text-slate-400 italic">{data?.honest_caveat}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
};


// ─────────────────────────────── T4 · Retry Cost Audit ───────────────────────────────
const RetryCostAudit = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paywall, setPaywall] = useState(null);
  const [days, setDays] = useState(180);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/retry-cost-audit/scan`, {
        headers: authHeaders(), params: { days },
      });
      setData(data);
      setPaywall(null);
    } catch (e) {
      if (e?.response?.status === 402) setPaywall(e.response.data?.detail || {});
      else toast.error('Could not run retry audit');
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="bg-slate-900/60 border-white/10" data-testid="retry-cost-audit-card">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Repeat className="w-4 h-4 text-amber-300" /> Retry Cost Audit
              <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">T4</Badge>
            </CardTitle>
            <CardDescription className="text-slate-400">
              Storms of near-duplicate calls (same customer + feature + model within 60s) — you paid for the retries.
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="border-white/10 text-slate-200" data-testid="retry-refresh-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
          </Button>
        </div>
        <div className="mt-2"><UsageBadge quota={data?.quota} /></div>
      </CardHeader>
      <CardContent className="space-y-4">
        {paywall ? (
          <PaywallCard
            featureName="Retry Cost Audit"
            message={paywall.message || "You've hit your free monthly limit. Upgrade to Pro for unlimited scans."}
            onUpgrade={() => { window.location.href = `${paywall.upgrade_url || '/pricing'}?from=token-intel&feature=retry-cost-audit`; }}
          />
        ) : loading && !data ? (
          <div className="text-slate-400 text-sm">Scanning…</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-950/50 rounded-lg p-3 border border-white/5">
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Storms</div>
                <div className="text-xl font-bold text-white mt-1" data-testid="retry-storms-count">{data?.storms_found || 0}</div>
              </div>
              <div className="bg-slate-950/50 rounded-lg p-3 border border-amber-500/20">
                <div className="text-[11px] uppercase tracking-wider text-amber-300">Wasted</div>
                <div className="text-xl font-bold text-amber-200 mt-1" data-testid="retry-wasted">{fmt(data?.total_wasted_usd)}</div>
              </div>
              <div className="bg-slate-950/50 rounded-lg p-3 border border-white/5">
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Features</div>
                <div className="text-xl font-bold text-white mt-1">{data?.features_affected || 0}</div>
              </div>
            </div>
            {data?.retry_storms?.length > 0 && (
              <div className="rounded-lg border border-white/5 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-950/60 text-[11px] uppercase tracking-wider text-slate-400">
                    <tr><th className="px-3 py-2 text-left">Customer</th><th className="px-3 py-2 text-left">Feature</th><th className="px-3 py-2 text-right">Retries</th><th className="px-3 py-2 text-right">Wasted</th></tr>
                  </thead>
                  <tbody>
                    {data.retry_storms.slice(0, 6).map((s, i) => (
                      <tr key={i} className="border-t border-white/5" data-testid={`retry-row-${i}`}>
                        <td className="px-3 py-2 text-slate-300">{s.customer_id}</td>
                        <td className="px-3 py-2 text-slate-300 font-mono text-xs">{s.feature_slug}</td>
                        <td className="px-3 py-2 text-right text-amber-300 font-mono">{s.retry_count}×</td>
                        <td className="px-3 py-2 text-right text-amber-200 font-mono">{fmt(s.wasted_usd, 4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-slate-400 italic">{data?.fix_recommendation}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
};


// ─────────────────────────────── T5 · Token Drift Alerter ────────────────────────────
const TokenDriftAlerter = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paywall, setPaywall] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/token-drift/scan`, {
        headers: authHeaders(), params: { weeks: 8, min_calls_per_week: 5 },
      });
      setData(data);
      setPaywall(null);
    } catch (e) {
      if (e?.response?.status === 402) setPaywall(e.response.data?.detail || {});
      else toast.error('Could not scan drift');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="bg-slate-900/60 border-white/10" data-testid="token-drift-card">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-300" /> Token Drift Alerter
              <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">T5</Badge>
            </CardTitle>
            <CardDescription className="text-slate-400">
              Features whose cost-per-call has crept upward over the last 8 weeks — the invisible context-window bloat.
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="border-white/10 text-slate-200" data-testid="drift-refresh-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
          </Button>
        </div>
        <div className="mt-2"><UsageBadge quota={data?.quota} /></div>
      </CardHeader>
      <CardContent className="space-y-4">
        {paywall ? (
          <PaywallCard
            featureName="Token Drift Alerter"
            message={paywall.message || "You've hit your free monthly limit. Upgrade to Pro for unlimited drift scans."}
            onUpgrade={() => { window.location.href = `${paywall.upgrade_url || '/pricing'}?from=token-intel&feature=token-drift`; }}
          />
        ) : loading && !data ? (
          <div className="text-slate-400 text-sm">Scanning…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-950/50 rounded-lg p-3 border border-amber-500/20">
                <div className="text-[11px] uppercase tracking-wider text-amber-300">Drifting features</div>
                <div className="text-xl font-bold text-amber-200 mt-1" data-testid="drift-count">{data?.drifting_features?.length || 0}</div>
              </div>
              <div className="bg-slate-950/50 rounded-lg p-3 border border-rose-500/20">
                <div className="text-[11px] uppercase tracking-wider text-rose-300">Extra annual cost</div>
                <div className="text-xl font-bold text-rose-200 mt-1" data-testid="drift-annual-extra">{fmt(data?.total_projected_extra_annual_usd)}</div>
              </div>
            </div>
            {data?.drifting_features?.length > 0 ? (
              <div className="space-y-2">
                {data.drifting_features.slice(0, 5).map((d, i) => (
                  <div key={i} className="bg-slate-950/50 rounded-lg p-3 border border-amber-500/20" data-testid={`drift-row-${i}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-mono text-amber-200">{d.feature_slug}</div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          Recent: ${d.recent_2wk_avg_cost_per_call}/call · Baseline: ${d.baseline_avg_cost_per_call}/call
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-rose-200">+{d.drift_pct}%</div>
                        <div className="text-xs text-slate-400">+{fmt(d.projected_extra_annual_usd)}/yr</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic">No features drifting up. Your prompts are stable.</p>
            )}
            <p className="text-xs text-slate-400 italic">{data?.fix_recommendation}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
};


// ───────────────────────── T8 · Context Window Optimizer ─────────────────────────────
const ContextWindowOptimizer = () => {
  const [prompt, setPrompt] = useState("You are a helpful AI assistant. Please always be kind, courteous, and respectful in your responses. Please make sure to always provide accurate and factually correct information. If you do not know the answer to something, please be honest and say so rather than making up information. Please always try to be as concise as possible while still being thorough.");
  const [monthlyCalls, setMonthlyCalls] = useState(50000);
  const [rate, setRate] = useState(0.003);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [paywall, setPaywall] = useState(null);

  const compress = async () => {
    setRunning(true);
    setResult(null);
    setPaywall(null);
    try {
      const { data } = await axios.post(`${API_URL}/context-optimizer/compress`, {
        system_prompt: prompt,
        monthly_calls: parseInt(monthlyCalls) || 1000,
        cost_per_1k_input_tokens_usd: parseFloat(rate) || 0.003,
      }, { headers: authHeaders() });
      setResult(data);
      if (data.skipped) {
        toast(data.message || 'No compression applied.');
      } else {
        toast.success(`Cut ${data.savings_pct}% · save ${fmt(data.annual_savings_usd)}/yr`);
      }
    } catch (e) {
      if (e?.response?.status === 402) setPaywall(e.response.data?.detail || {});
      else toast.error(e?.response?.data?.detail || 'Compression failed');
    } finally { setRunning(false); }
  };

  const copy = async () => {
    const ok = await safeCopyToClipboard(result?.compressed_prompt || '');
    if (ok) toast.success('Copied compressed prompt');
    else toast.error('Copy failed — please select & copy manually');
  };

  return (
    <Card className="bg-slate-900/60 border-white/10" data-testid="context-optimizer-card">
      <CardHeader>
        <CardTitle className="text-base text-white flex items-center gap-2">
          <Minimize2 className="w-4 h-4 text-emerald-300" /> Context Window Optimizer
          <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">T8</Badge>
        </CardTitle>
        <CardDescription className="text-slate-400">
          Paste a bloated system prompt → Claude Sonnet 4.5 returns a compressed version (typically 50-75% shorter).
        </CardDescription>
        <div className="mt-2"><UsageBadge quota={result?.quota} /></div>
      </CardHeader>
      <CardContent className="space-y-4">
        {paywall ? (
          <PaywallCard
            featureName="Context Window Optimizer"
            message={paywall.message || "You've used your 3 free compressions this month. Upgrade for unlimited."}
            onUpgrade={() => { window.location.href = `${paywall.upgrade_url || '/pricing'}?from=token-intel&feature=context-optimizer`; }}
          />
        ) : (
          <>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-400">Your system prompt</Label>
          <Textarea
            rows={5}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            data-testid="optimizer-prompt-input"
            className="bg-slate-950 border-white/10 font-mono text-xs mt-1.5"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-400">Monthly calls</Label>
            <Input type="number" value={monthlyCalls} onChange={(e) => setMonthlyCalls(e.target.value)} data-testid="optimizer-calls-input" className="bg-slate-950 border-white/10 mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-400">$ / 1K input tokens</Label>
            <Input type="number" step="0.0001" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="optimizer-rate-input" className="bg-slate-950 border-white/10 mt-1.5" />
          </div>
        </div>
        <Button
          onClick={compress}
          disabled={running || !prompt.trim()}
          className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
          data-testid="optimizer-compress-btn"
        >
          {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Compressing</>
            : <><Sparkles className="w-4 h-4 mr-2" /> Compress prompt</>}
        </Button>

        {result && !result.skipped && (
          <div className="space-y-3 pt-2 border-t border-white/5">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-950/50 rounded-lg p-3 border border-white/5">
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Original</div>
                <div className="text-xl font-bold text-white mt-1" data-testid="optimizer-original-tokens">{result.original_tokens} tok</div>
              </div>
              <div className="bg-slate-950/50 rounded-lg p-3 border border-emerald-500/30">
                <div className="text-[11px] uppercase tracking-wider text-emerald-300">Compressed</div>
                <div className="text-xl font-bold text-emerald-200 mt-1" data-testid="optimizer-compressed-tokens">{result.compressed_tokens} tok</div>
              </div>
              <div className="bg-slate-950/50 rounded-lg p-3 border border-emerald-500/30">
                <div className="text-[11px] uppercase tracking-wider text-emerald-300">Savings</div>
                <div className="text-xl font-bold text-emerald-200 mt-1" data-testid="optimizer-savings-pct">{result.savings_pct}%</div>
              </div>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-lg p-3">
              <div className="text-xs uppercase tracking-wider text-emerald-200">Projected annual savings</div>
              <div className="text-2xl font-bold text-white mt-1" data-testid="optimizer-annual-savings">{fmt(result.annual_savings_usd)}</div>
            </div>
            <div className="relative">
              <Textarea
                readOnly
                value={result.compressed_prompt}
                rows={5}
                data-testid="optimizer-compressed-output"
                className="bg-slate-950 border-emerald-400/30 font-mono text-xs"
              />
              <Button
                onClick={copy}
                size="sm"
                variant="outline"
                className="absolute top-2 right-2 border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10"
                data-testid="optimizer-copy-btn"
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
              </Button>
            </div>
            <p className="text-xs text-slate-400 italic">{result.honest_caveat}</p>
          </div>
        )}
        {result?.skipped && (
          <div className="bg-slate-950/50 rounded-lg p-3 border border-white/5 text-sm text-slate-400">{result.message}</div>
        )}
        </>
        )}
      </CardContent>
    </Card>
  );
};


// ───────────────────── T10 · Streaming-vs-Batch Cost Diff ────────────────────────────
const StreamingBatchDiff = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paywall, setPaywall] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/streaming-batch-diff/scan`, {
        headers: authHeaders(), params: { days: 30 },
      });
      setData(data);
      setPaywall(null);
    } catch (e) {
      if (e?.response?.status === 402) setPaywall(e.response.data?.detail || {});
      else toast.error('Could not scan abandoned streams');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="bg-slate-900/60 border-white/10" data-testid="streaming-batch-card">
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Pause className="w-4 h-4 text-emerald-300" /> Streaming-vs-Batch Cost Diff
              <Badge variant="outline" className="border-emerald-400/40 text-emerald-300 text-[11px]">T10</Badge>
            </CardTitle>
            <CardDescription className="text-slate-400">
              Likely-abandoned streams — short observed outputs vs. feature's normal length. You paid; the user didn't read.
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="border-white/10 text-slate-200" data-testid="streaming-refresh-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
          </Button>
        </div>
        <div className="mt-2"><UsageBadge quota={data?.quota} /></div>
      </CardHeader>
      <CardContent className="space-y-4">
        {paywall ? (
          <PaywallCard
            featureName="Streaming-vs-Batch Cost Diff"
            message={paywall.message || "You've hit your free monthly limit. Upgrade to Pro for unlimited scans."}
            onUpgrade={() => { window.location.href = `${paywall.upgrade_url || '/pricing'}?from=token-intel&feature=streaming-batch-diff`; }}
          />
        ) : loading && !data ? (
          <div className="text-slate-400 text-sm">Scanning…</div>
        ) : data?.abandoned_by_feature?.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-950/50 rounded-lg p-3 border border-amber-500/20">
                <div className="text-[11px] uppercase tracking-wider text-amber-300">Wasted (30d)</div>
                <div className="text-xl font-bold text-amber-200 mt-1" data-testid="streaming-wasted">{fmt(data.total_wasted_usd)}</div>
              </div>
              <div className="bg-slate-950/50 rounded-lg p-3 border border-rose-500/20">
                <div className="text-[11px] uppercase tracking-wider text-rose-300">Projected annual</div>
                <div className="text-xl font-bold text-rose-200 mt-1" data-testid="streaming-annual">{fmt(data.projected_annual_wasted_usd)}</div>
              </div>
            </div>
            <div className="space-y-2">
              {data.abandoned_by_feature.slice(0, 5).map((f, i) => (
                <div key={i} className="bg-slate-950/50 rounded-lg p-3 border border-white/5" data-testid={`streaming-row-${i}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-mono text-emerald-300">{f.feature_slug}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{f.likely_abandoned_count} / {f.total_events} likely abandoned</div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-amber-200">{f.abandon_rate_pct}%</div>
                      <div className="text-xs text-slate-400">{fmt(f.projected_annual_wasted_usd)}/yr</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 italic">{data.fix_recommendation}</p>
          </>
        ) : (
          <div className="bg-slate-950/50 rounded-lg p-4 border border-white/5 text-sm text-slate-400" data-testid="streaming-empty">
            No abandoned-stream pattern detected. Users are reading the full output, or your features have stable short outputs.
          </div>
        )}
      </CardContent>
    </Card>
  );
};




const SeedButton = () => {
  const [seeding, setSeeding] = useState(false);
  const handleSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API_URL}/token-intel-seed/seed`, {}, { headers: authHeaders() });
      toast.success(`Seeded ${data.inserted} events. Refreshing…`);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
      setSeeding(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={handleSeed}
      disabled={seeding}
      className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10"
      data-testid="token-intel-seed-btn"
    >
      {seeding ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Seeding</>
        : <><Wand2 className="w-3.5 h-3.5 mr-1.5" /> Try with sample data</>}
    </Button>
  );
};

const TokenIntelligence = () => {
  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="token-intelligence-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
            <span className="text-[11px] font-semibold tracking-widest text-emerald-200 uppercase">
              Token Intelligence
            </span>
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight"><BrandTitle>Stop your tokens from leaking.</BrandTitle></h1>
          <p className="text-slate-400 text-sm mt-1.5 max-w-2xl">
            The three deepest token-cost surfaces in FinOps: runaway leak detection, Anthropic prompt-cache
            opportunities, and the per-customer × per-feature heatmap. No other tool combines these.
          </p>
        </div>
        <SeedButton />
      </header>

      <MultiModelRouter />
      <LeakDetector />
      <CacheMissDetective />
      <Heatmap />
      <RetryCostAudit />
      <TokenDriftAlerter />
      <ContextWindowOptimizer />
      <StreamingBatchDiff />

      <Card className="bg-emerald-500/10 border-emerald-500/30">
        <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row items-start justify-between gap-3">
          <div>
            <p className="text-white font-semibold">Want to estimate model swap savings before shipping?</p>
            <p className="text-slate-400 text-sm mt-1">
              Try the public Prompt Cost Predictor — paste any prompt, see all 11 models compared.
              Or run the Fine-tuning ROI calculator to know when fine-tuning beats prompting.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/token-intelligence-suite">
              <Button variant="outline" className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10" data-testid="token-intel-suite-cta">
                See all 10 features <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </Link>
            <Link to="/prompt-cost-predictor">
              <Button variant="outline" className="border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10" data-testid="token-intel-pcp-cta">
                Prompt Cost Predictor <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </Link>
            <Link to="/fine-tuning-roi">
              <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="token-intel-ftr-cta">
                Fine-tuning ROI <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TokenIntelligence;
